import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Ban, TriangleAlert } from 'lucide-react';
import { z } from 'zod';
import { Form } from '@/components/ui/form';
import { PCMP_REJECTED_AT_STAGE_LABELS } from '../../constants/pcmpHold.constants';
import { PCMP_MKT_COMMENT_MAX } from '../../constants/pcmpProposal.constants';
import { useAdminRejectPcmpProposal } from '../../hooks/useAdminRejectPcmpProposal';
import type { PcmpRejectedAtStage } from '../../types/pcmpProposal.types';
import { PcmpCommentField } from '../PcmpMarketingActions/PcmpCommentField';
import { PcmpEditDialog } from '../PcmpProposalEdit/PcmpEditDialog';

const reasonFormSchema = z.object({
  reason: z.string().trim().min(1, 'A rejection reason is required.').max(PCMP_MKT_COMMENT_MAX),
});

type ReasonFormValues = z.infer<typeof reasonFormSchema>;

interface PcmpAdminRejectDialogProps {
  proposalId: number;
  refNo: string;
  /** Where the reject would catch it; the API derives the same. */
  stage: PcmpRejectedAtStage | null;
  onClose: () => void;
}

/** Legacy admin reject (LDS-721): closes the proposal for good before payout. */
export function PcmpAdminRejectDialog({
  proposalId,
  refNo,
  stage,
  onClose,
}: PcmpAdminRejectDialogProps) {
  const form = useForm<ReasonFormValues>({
    resolver: zodResolver(reasonFormSchema),
    defaultValues: { reason: '' },
  });
  const { mutate, isPending, error } = useAdminRejectPcmpProposal(proposalId, onClose);

  const onSubmit = form.handleSubmit((values) => mutate(values.reason));

  return (
    <Form {...form}>
      <PcmpEditDialog
        open
        onOpenChange={(open) => !open && onClose()}
        title="Admin Reject"
        description={refNo}
        icon={Ban}
        isDirty={form.formState.isDirty}
        isPending={isPending}
        error={error}
        onSubmit={(event) => void onSubmit(event)}
        submitLabel="Reject proposal"
        submitVariant="destructive"
        pendingLabel="Rejecting..."
      >
        <div className="flex gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm">
          <TriangleAlert className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
          <span>
            Admin reject is final: the proposal is closed
            {stage ? ` at ${PCMP_REJECTED_AT_STAGE_LABELS[stage]}` : ''}, taken out of any batch,
            and no further action can be taken on it.
          </span>
        </div>
        <PcmpCommentField
          control={form.control}
          name="reason"
          label="Reason"
          required
          placeholder="Why the proposal is rejected"
          description="Recorded on the proposal's timeline."
        />
      </PcmpEditDialog>
    </Form>
  );
}
